import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
import React from 'react';
import {
    Box,
    Typography,
    Avatar,
    IconButton,
    TextField,
    Stack,
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';

import { cartHelpers, productHelpers } from '../../services/ecommerceApi';

const CartItemLine = ({
    item,
    compact = false,
    editable = false,
    disabled = false,
    onIncrease,
    onDecrease,
    onSetQuantity,
    onRemove,
}) => {
  const { t } = useTranslation();
    const product = item.product || item.kit;
    const image = productHelpers.getMainImage(product);
    const sku = product?.sku || item.sku || null;
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unit_price || 0);
    const lineSubtotal = Number(item.total_price || (unitPrice * quantity));
    const comparePrice = Number(product?.compare_price || 0);
    const hasComparePrice = comparePrice > unitPrice;

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1.5,
                py: compact ? 1.25 : 1.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Avatar
                src={image}
                alt={item.item_name}
                variant="rounded"
                sx={{
                    width: compact ? 56 : 64,
                    height: compact ? 56 : 64,
                    bgcolor: 'grey.100',
                    flexShrink: 0,
                }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant={compact ? 'subtitle2' : 'subtitle1'} fontWeight={700} sx={{ lineHeight: 1.25 }}>
                    {item.item_name}
                </Typography>

                {sku && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        SKU: {sku}
                    </Typography>
                )}

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, mb: 0.75 }}>
                    <Typography variant="subtitle2" color="primary" fontWeight={700}>
                        {cartHelpers.formatCurrency(unitPrice)}
                    </Typography>
                    {hasComparePrice && (
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                            {cartHelpers.formatCurrency(comparePrice)}
                        </Typography>
                    )}
                </Stack>

                {editable ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
                        <IconButton size="small" onClick={onDecrease} disabled={disabled || quantity <= 1}>
                            <RemoveIcon />
                        </IconButton>
                        <TextField
                            size="small"
                            value={quantity}
                            onChange={(e) => {
                                const next = parseInt(e.target.value, 10);
                                onSetQuantity?.(Number.isFinite(next) && next > 0 ? next : 1);
                            }}
                            inputProps={{
                                min: 1,
                                type: 'number',
                                style: { textAlign: 'center', width: '50px' },
                            }}
                            disabled={disabled}
                        />
                        <IconButton size="small" onClick={onIncrease} disabled={disabled}>
                            <AddIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={onRemove} disabled={disabled}>
                            <DeleteIcon />
                        </IconButton>
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('hardcoded.components_store_cartitemline.cantidad')}{quantity}
                    </Typography>
                )}

                <Typography variant="subtitle2" fontWeight={700}>
                    {i18n.t('hardcoded.components_admin_kitform.subtotal')}{cartHelpers.formatCurrency(lineSubtotal)}
                </Typography>

                {item.configuration?.valve_mix && Array.isArray(item.configuration.valve_mix) && (
                    <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('hardcoded.components_store_cartitemline.valvulas_del_kit')}</Typography>
                        {item.configuration.valve_mix.map((mixLine, idx) => (
                            <Typography key={`${item.id}-mix-${idx}`} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {mixLine.quantity} {mixLine.name || mixLine.sku || t('hardcoded.components_store_cartitemline.valvula')}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CartItemLine;
